import { enums } from "@shared/constants";
import { encodeData } from "@shared/utils/urls/url-utils";
import { TrackTokenDataType, TrackingToken } from "./types/analytics-types";
import gtag from "./gtag";

/**
 * Triggers a tracking pixel via GET request
 * Used for tracking tokens that require GET requests (e.g., TR_SIMPLE, TR_TRADABLE_BITS)
 *
 * For platforms that support a POST request, use `navigator.sendBeacon`.
 * @see https://github.com/w3c/beacon/pull/27#issuecomment-241549321
 */
const triggerPixel = (url: string): Promise<Response> => {
  return fetch(url, {
    keepalive: true,
    mode: "no-cors",
    cache: "no-store",
    referrerPolicy: "no-referrer-when-downgrade",
  });
};

/**
 * Given a list of Tracking Tokens, fires a beacon to every token with the supplied data
 */
export const triggerTokens = async (
  allTokens: TrackingToken[],
  data: TrackTokenDataType
) => {
  const beaconFormData = new FormData();
  if (data) {
    Object.keys(data).forEach((key) => beaconFormData.append(key, data[key]));
  }

  const tokenPromises = [];

  allTokens.forEach((token) => {
    switch (token.token_type) {
      case enums.TRACKING_TOKEN_TYPES.TR_SIMPLE.id: {
        tokenPromises.push(triggerPixel(token.tracking_url));
        break;
      }
      case enums.TRACKING_TOKEN_TYPES.TR_FACEBOOK.id: {
        const url = encodeData("https://www.facebook.com/tr", {
          id: token.tracking_id,
          ev: data?.fbEvent,
          "cd[value]": data?.value,
          "cd[currency]": data?.currency,
          "cd[content_name]": data?.name,
          "cd[content_ids]": data?.content_ids,
          "cd[content_type]": data?.content_type,
          noscript: 1,
        });

        tokenPromises.push(navigator.sendBeacon(url, beaconFormData));
        break;
      }
      case enums.TRACKING_TOKEN_TYPES.TR_TWITTER.id: {
        const url = encodeData("https://analytics.twitter.com/i/adsct", {
          txn_id: token.tracking_id,
          p_id: "Twitter",
          tw_sale_amount: data.value,
        });

        tokenPromises.push(navigator.sendBeacon(url, beaconFormData));
        break;
      }
      case enums.TRACKING_TOKEN_TYPES.TR_GOOGLE_ADWORDS.id: {
        tokenPromises.push(
          gtag.awConversion(token.tracking_id, token.tracking_label, {
            value: data.value,
            currency: data.currency,
            transaction_id: data.transaction_id,
          })
        );
        break;
      }
      case enums.TRACKING_TOKEN_TYPES.TR_TRADABLE_BITS.id: {
        // Tradable bits currently only supports "purchase" events, which translate to the post-checkout location
        if (
          token.location ===
          enums.TRACKING_TOKEN_LOCATIONS.LOCATION_POST_CHECKOUT.id
        ) {
          // If items array exists, fire one pixel per item
          if (data.items && data.items.length > 0) {
            data.items.forEach((item) => {
              const url = encodeData(
                `https://tradablebits.com/pixels/${token.tracking_id}/event`,
                {
                  event_name: "purchase",
                  event_amount: item.eventAmount,
                  order_key: `${data.transaction_id}-${item.itemId}`,
                  purchase_event_name: item.name,
                  quantity: item.quantity,
                  purchase_event_details: item.variant,
                  currency: data.currency,
                  venue_id: data.venue_id,
                }
              );

              tokenPromises.push(triggerPixel(url));
            });
          } else {
            // Backward compatibility: single aggregate event
            const url = encodeData(
              `https://tradablebits.com/pixels/${token.tracking_id}/event`,
              {
                event_name: "purchase",
                event_amount: data.value,
                order_key: data.transaction_id,
              }
            );

            tokenPromises.push(triggerPixel(url));
          }
        } else {
          // eslint-disable-next-line no-console
          console.warn(
            `Unsupported location: ${token.location}, for token type: ${token.token_type}`
          );
        }
        break;
      }
      default: {
        // eslint-disable-next-line no-console
        console.error("Unknown tracking token type:", token);

        // TODO: move sentry to /core
        // logAppInfo(`Unknown tracking token type: ${token}`);
      }
    }
  });

  const results = await Promise.allSettled(tokenPromises);
  results.forEach((result) => {
    if (result.status === "rejected") {
      // eslint-disable-next-line no-console
      console.error(result);
    }
  });
};
